import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'lucide-react';
import ProductCard from '../ProductCard';
import './PromosSection.css';

function PromosSection({
  promoItems,
  onAddToCart,
  onItemClick,
  getTranslatedText,
  title = 'Ofertas del día',
  showViewAll = false,
  onViewAll,
}) {
  const { t } = useTranslation();
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalDots, setTotalDots] = useState(0);

  const calculateDots = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      setTotalDots(0);
      return;
    }
    // Each dot represents one "page" of visible width
    const pages = Math.ceil(el.scrollWidth / el.clientWidth);
    setTotalDots(pages);
  }, []);

  const updateActiveIndex = useCallback(() => {
    const el = carouselRef.current;
    if (!el || totalDots <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;
    const progress = el.scrollLeft / maxScroll;
    const index = Math.round(progress * (totalDots - 1));
    setActiveIndex(index);
  }, [totalDots]);

  useEffect(() => {
    calculateDots();
    window.addEventListener('resize', calculateDots);
    return () => window.removeEventListener('resize', calculateDots);
  }, [calculateDots, promoItems]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => updateActiveIndex();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [updateActiveIndex]);

  const handleDotClick = (index) => {
    const el = carouselRef.current;
    if (!el || totalDots <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const scrollTo = (index / (totalDots - 1)) * maxScroll;
    el.scrollTo({ left: scrollTo, behavior: 'smooth' });
  };

  if (!promoItems || promoItems.length === 0) {
    return null;
  }

  return (
    <section className="promos-section">
      <div className="promos-header">
        <h2 className="promos-title">
          <Tag size={20} aria-hidden="true" />
          {title}
        </h2>
        {showViewAll && onViewAll && (
          <button className="promos-view-all" onClick={onViewAll}>
            {t('promos.viewAll')}
            <span className="material-icons" aria-hidden="true">chevron_right</span>
          </button>
        )}
      </div>

      <div className="promos-carousel" ref={carouselRef}>
        {promoItems.map((item) => (
          <div key={item.itemId} className="promo-card-wrapper">
            <ProductCard
              item={item}
              viewMode="grid"
              onAddToCart={onAddToCart}
              onItemClick={onItemClick}
              getTranslatedText={getTranslatedText}
            />
          </div>
        ))}
      </div>

      {totalDots > 1 && (
        <div className="promos-dots" role="tablist" aria-label="Carousel pages">
          {Array.from({ length: totalDots }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`promos-dot${i === activeIndex ? ' promos-dot--active' : ''}`}
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Page ${i + 1}`}
              onClick={() => handleDotClick(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default PromosSection;
